export const createEndpoint = (template, params = {}) => {
  let endpoint = template;

  // Replace URL parameters (:param)
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      endpoint = endpoint.replace(`:${key}`, value);
    }
  });

  // Replace query parameters ({{param}})
  Object.entries(params).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    if (endpoint.includes(placeholder)) {
      if (Array.isArray(value) && value.length > 0) {
        // Repeated-key format for multi-value filters, e.g. a merged tab's
        // several underlying statuses — {{status}} with
        // ['Contacted', 'Acknowledged'] against a template segment of
        // `&status={{status}}` becomes `&status=Contacted&status=Acknowledged`,
        // since the `status=` the placeholder sits after is already part of
        // the template.
        endpoint = endpoint.replace(
          placeholder,
          value.map((v) => encodeURIComponent(v)).join(`&${key}=`)
        );
      } else if (
        value !== undefined &&
        value !== null &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        endpoint = endpoint.replace(placeholder, encodeURIComponent(value));
      } else {
        // Remove the entire query parameter segment if value is undefined/null
        // Matches patterns like &param_name={{key}} or ?param_name={{key}}
        const escapedPlaceholder = placeholder.replace(/[{}]/g, '\\$&');
        // Remove &key=value pattern
        endpoint = endpoint.replace(
          new RegExp(`&[^&=?]+=${escapedPlaceholder}`),
          ''
        );
        // Remove ?key=value& pattern (first param followed by another)
        endpoint = endpoint.replace(
          new RegExp(`\\?[^&=?]+=${escapedPlaceholder}&`),
          '?'
        );
        // Remove ?key=value pattern (only param)
        endpoint = endpoint.replace(
          new RegExp(`\\?[^&=?]+=${escapedPlaceholder}$`),
          ''
        );
      }
    }
  });

  return endpoint;
};

export const API_ENDPOINTS = {
  AUTH: {
    VERIFY: '/auth/verify',
    REFRESH: '/internal/auth/refresh-token',
    RESET_PASSWORD: '/internal/auth/reset-password',
    FORGOT_PASSWORD: '/internal/auth/forgot-password',
  },
  USER: {
    LOGIN: '/internal/auth/login',
    LOGOUT: '/internal/auth/logout',
    CHANGE_PASSWORD: '/internal/auth/change-password',
    GET_AHEDGE_USERS:
      '/internal/admin/ahedge-users?role={{role}}&page={{pageNo}}&pageSize={{pageSize}}',
    CREATE_AHEDGE_USER: '/internal/admin/register-ahedge-user',
    REGISTER_ORGANIZATION_USER: '/internal/admin/customer/add-org-members',
    DEACTIVATE_USER: '/internal/admin/ahedge-users/:id/deactivate',
    ACTIVATE_USER: '/internal/admin/ahedge-users/:id/activate',
    // Backend doesn't accept a role at user-creation time (CREATE_AHEDGE_USER
    // above creates a role-less account) — roles are assigned afterward as
    // this separate action. Method (PUT) and payload ({ roles: [...] })
    // confirmed — matched an earlier best guess exactly. The path's
    // `/internal` prefix is this app's own 100%-consistent convention
    // (every endpoint in this file has it, and it's not part of the axios
    // baseURL — confirmed in axios.config.jsx, so it really is repeated
    // per-endpoint) rather than confirmed for this specific one: the given
    // contract omitted it, but a live PUT to both
    // /internal/admin/ahedge-users/:id/roles and
    // /admin/ahedge-users/:id/roles 404'd identically on api-dev, meaning
    // the route isn't deployed there yet either way — no live signal to
    // settle it. Kept the prefix on the convention prior; drop it if a real
    // deploy says otherwise.
    ASSIGN_ROLES: '/internal/admin/ahedge-users/:id/roles',
  },
  PERSONA: {
    GETPERSONAMASTERDATA: '/internal/reference-data',
    CREATEPERSONA: '/internal/personas',
    UPDATEPERSONA: '/internal/personas/:id',
    LISTALLPERSONAS: '/internal/personas?page={{page}}&pageSize={{pageSize}}',
    LISTPERSONACBYCUSTOMER: '/personas?customer_type=={{ctype}}',
    GETPERSONAINFO: '/internal/personas/:id',
  },
  OPPORTUNITY: {
    GETOPPORTUNITIESBYSTATUS: '/internal/requirements?status={{status}}',
    GETOPPORTUNITYDATA: '/internal/requirements/:id/details',
    GETOPPORTUNITYLOCATIONDATA:
      '/internal/requirements/:id/requirement-locations/:reqLocId/preview',
    GETASSIGNMENTLOCATIONS:
      '/internal/assignments/:assignmentId/assignment-locations',
    GETASSIGNMENTPREVIEW: '/internal/assignments/:assignmentId/preview',
    GETBASICOPPORTUNITYINFO:
      '/internal/requirements/:id/requirement-locations/:reqLocId',
    GETREQUIREMENTLOCATIONS: '/internal/requirements/:id/requirement-locations',
    GETOPPORTUNITIESCSE:
      '/internal/requirements?status={{status}}&page={{pageNo}}&pageSize={{pageSize}}',
    // Method/path per explicit update — was scoped to one requirement
    // location (:id/requirement-locations/:reqLocId/claim); now claims the
    // whole requirement in one call, no reqLocId.
    CLAIMOPPORTUNITY: '/internal/requirements/:id/claim',
    // CSE forwards a requirement directly to the open TA pool — no specific
    // TA/TA Lead is targeted, any active TA can see and submit against it.
    ASSIGNTOTA: '/internal/requirements/:id/assignment',
    GETAVAILABLEFLOWS: '/internal/flows/available',
    GETOPPORTUNITIESTA: '/internal/assignments',
    GETHISTORY: '/internal/requirements/:id/audit',
    PUBLISH_ASSIGNMENT: '/internal/assignments/:assignmentId/publish',
    // Path updated per explicit backend change — was
    // /internal/requirements/dashboard/metrics; every dashboard endpoint now
    // lives under /internal/dashboard/... instead of nested under its own
    // resource.
    GETCSEDASHBOARDMETRICS:
      '/internal/dashboard/requirements/metrics?period={{period}}',
    // Path updated per the same change — was
    // /internal/requirements/dashboard/high-priority-requirements.
    GETCSEHIGHPRIORITYREQUIREMENTS:
      '/internal/dashboard/requirements/high-priority-requirements',
    // New — the Dashboard's Assignment Overview section's real backing
    // endpoint (was mocked, see Dashboard.jsx's MOCK_ASSIGNMENT_* constants'
    // own comment). Same `period` query param as GETCSEDASHBOARDMETRICS.
    GETASSIGNMENTDASHBOARDMETRICS:
      '/internal/dashboard/assignments/metrics?period={{period}}',
    // New — the Assignment Overview's own "Urgent Dues" equivalent.
    GETASSIGNMENTHIGHPRIORITY:
      '/internal/dashboard/assignments/high-priority-assignments',
    // New — team-scoped variants of the two above (presumably: every
    // assignment across every TA this CSE manages, vs. just the CSE's own).
    // Not wired into any UI yet — added for when/if a team-level section is
    // built; see service layer's own comment.
    GETASSIGNMENTTEAMDASHBOARDMETRICS:
      '/internal/dashboard/assignments/team/metrics?period={{period}}',
    GETASSIGNMENTTEAMHIGHPRIORITY:
      '/internal/dashboard/assignments/team/high-priority-assignments',
    GETTASUMMARY: '/internal/assignments/summary',
    GETCSEREQUIREMENTSSUMMARY: '/internal/requirements/summary',
    CREATE_MOCK_REQUIREMENT: '/internal/mock-requirements',
    ASSIGN_PARTNER_TO_ASSIGNMENT:
      '/internal/assignments/:assignmentId/partners',
    GET_ASSIGNMENT_PARTNERS: '/internal/assignments/:assignmentId/partners',
    GET_ASSIGNMENT_INSTITUTIONS:
      '/internal/assignments/:assignmentId/institutions',
    GET_FLOW_STATUSES: '/internal/assignments/:assignmentId/flow-statuses',
    // Every configured flow (Entry Level/Senior Level/...), independent of
    // any one requirement — distinct from GETAVAILABLEFLOWS above (that one
    // is the requirement-scoped "Forward to TA" modal's picker, a different
    // path: /internal/requirements/available-flows). Powers the Dashboard's
    // Assignment Status flow dropdown.
    GET_FLOWS: '/internal/flows/available',
    // Same flow-statuses resource as GET_FLOW_STATUSES above, addressed
    // directly by a flow's own state_machine_key instead of resolving it
    // indirectly through one assignment — for contexts with no real
    // assignment in hand yet (the Dashboard's flow picker).
    GET_FLOW_STATUSES_BY_KEY:
      '/internal/available-flows/:stateMachineKey/flow-statuses',
  },
  JOB: {
    LIST: '/internal/jobs',
    GET: '/internal/jobs/:id',
    GET_JOB_INSTITUTIONS: '/internal/jobs/:jobCode/institutions',
  },
  SEARCH: {
    SEARCHCANDIDATES:
      '/internal/candidates/search?email={{email}}&phone={{contact}}&name={{name}}&page={{pageNo}}&pageSize={{pageSize}}',
    GETCANDIDATEDETAILS: '/internal/candidates/:id/profile',
    SUGGESTCANDIDATES:
      '/internal/candidates/suggest?name={{name}}&limit={{limit}}',
  },
  TEST: {
    DEACTIVATETEST: '/internal/tests/:id/deactivate',
    NEWTEST: '/internal/tests',
    UPDATETEST: '/internal/tests/:id',
    LISTTESTS:
      '/internal/tests?page={{pageNo}}&pageSize={{pageSize}}&search={{search}}&status={{status}}',
    LISTALLTESTS:
      '/internal/tests?page={{pageNo}}&pageSize={{pageSize}}&search={{search}}&status={{status}}',
    GETTEST: '/internal/tests/:id',
    PUBLISHTEST: '/internal/tests/:id/publish',
  },
  TESTGROUP: {
    DEACTIVATETESTGROUP: '/internal/groups/:id/deactivate',
    NEWTESTGROUP: '/internal/groups',
    UPDATETESTGROUP: '/internal/groups/:id',
    LISTTESTSGROUP:
      '/internal/groups?page={{pageNo}}&pageSize={{pageSize}}&search={{search}}&status={{status}}',
    LISTALLTESTSGROUP:
      '/internal/groups?page={{pageNo}}&pageSize={{pageSize}}&search={{search}}&status={{status}}',
    GETTESTGROUP: '/internal/groups/:id',
    PUBLISHTESTGROUP: '/internal/groups/:id/publish',
  },
  TESTCOLLECTION: {
    DEACTIVATETESTCOLLECTION: '/internal/collections/:id/deactivate',
    NEWTESTCOLLECTION: '/internal/collections',
    UPDATETESTCOLLECTION: '/internal/collections/:id',
    LISTTESTSCOLLECTION:
      '/internal/collections?page={{pageNo}}&pageSize={{pageSize}}&search={{search}}&status={{status}}',
    GETTESTCOLLECTION: '/internal/collections/:id',
    PUBLISHTESTCOLLECTION: '/internal/collections/:id/publish',
    UPDATECOLLECTIONRULES: '/internal/collections/:id/rules',
  },
  TESTSESSION: {
    NEWTESTSESSION: '/internal/session',
  },
  CANDIDATES: {
    LIST: '/internal/requirements/:id/candidates/source-candidates?requirementLocationId={{requirementLocationId}}&page={{pageNo}}&pageSize={{pageSize}}',
    SUBMITCANDIDATE: '/internal/requirements/:id/candidates/source-candidates',
    LISTBYSTATUS:
      '/internal/requirements/:id/candidates?requirementLocationId={{requirementLocationId}}&status={{status}}&page={{pageNo}}&pageSize={{pageSize}}',
    DELETECANDIDATE: '/internal/requirements/:id/candidates',
    LISTBYSTATUSFORTA:
      '/internal/assignments/:assignmentId/candidates-pipeline/candidates?requirementLocationId={{requirementLocationId}}&status={{status}}&page={{pageNo}}&pageSize={{pageSize}}&search={{search}}',
    LISTEXTERNALCANDIDATES:
      '/internal/assignments/:assignmentId/candidates/external?source={{source}}&page={{pageNo}}&pageSize={{pageSize}}',
    LISTFROMSECONDARY:
      '/internal/assignments/:assignmentId/candidates/source-candidates?requirementLocationId={{requirementLocationId}}',
    GETCANDIDATESCORES:
      '/internal/session/requirements/:requirement_id/candidates/:candidate_id',
    UPDATESCORE: '/internal/session/score',
    SENDTESTLINK: '/internal/session',
    ASSIGNTEST: '/internal/assign-test',
    // Backend moved these off /internal/candidates/:secMappingId/... onto a
    // dedicated /internal/assignments/candidates-pipeline/:secMappingId/...
    // base — same param, same trailing path, just a new prefix.
    EXECUTE_FLOW_ACTION:
      '/internal/assignments/candidates-pipeline/:secMappingId/flow-actions/:actionKey',
    SKILL_MATCH:
      '/internal/assignments/candidates-pipeline/:secMappingId/skill-match',
    // Prefill source for the reschedule_slot modal (senior_level_flow) —
    // now keyed by secMappingId alone, same shape as EXECUTE_FLOW_ACTION/
    // SKILL_MATCH above (dropped the earlier :assignmentId/candidates/
    // :candidateId shape entirely, not just the candidates→candidates-
    // pipeline rename).
    GET_INTERVIEW_SCHEDULE:
      '/internal/assignments/candidates-pipeline/:secMappingId/interview-schedule',
    COMPLETECANDIDATESCORE:
      '/internal/requirements/:requirement_id/candidates/:candidate_id/calculate-scores',
    SUBMITSECONDARYCANDIDATE:
      '/internal/assignments/:assignmentId/candidates/source-candidates',
    UPDATETESTSTATUS: '/internal/candidate/update-test-status',
    SAVEJOININGDETAILS: '/internal/billing/candidates/joining',
    VERIFYJOININGDETAILS: '/internal/billing/candidates/verify-joining',
    GETCANDIDATEDPROFILE:
      '/internal/candidates/:id/profile?requirement_id={{opportunityId}}',
    GETRESUMEPDF:
      '/internal/candidates/:candidate_id/resume-pdf?masked={{masked}}',
    GETBYCONTACT:
      '/internal/candidates/profile?phone={{phone}}&email={{email}}',
    REFRESHSESSION: '/internal/session/refresh',
    GETAUDITLOGS:
      '/internal/candidates/:id/audit?page={{pageNo}}&pageSize={{pageSize}}&search={{search}}&event_category={{eventCategory}}&actor_type={{actorType}}&sort_by={{sortBy}}&sort={{sortOrder}}',
    GETAUDITCONFIG: '/internal/audit/config',
    BULKUPDATEFROMTASUBMISSION:
      '/internal/requirements/:id/candidates/bulk-update',
    REQUIREMENT_MATCHES:
      '/internal/requirements/:id/matches?page={{page}}&pageSize={{pageSize}}&search={{search}}&tier={{tier}}',
    CONSTRAINT_MODES: '/internal/requirements/:id/constraint-modes',
    SET_CONSTRAINT_MODE: '/internal/requirements/:id/constraint-modes/:field',
  },
  APPSETTINGS: {
    LISTAPPSETTINGS: '/internal/settings',
    CREATEAPPSETTING: '/internal/settings',
    UPDATEAPPSETTING: '/internal/settings/:parameter/value',
  },
  ORGANIZATION: {
    CREATEORGANIZATION: '/internal/admin/register-org',
    UPDATEORGANIZATION: '/internal/admin/organizations/:id',
    LISTALLORGANIZATIONS:
      '/internal/admin/organizations?page={{pageNo}}&pageSize={{pageSize}}',
    GETRMLIST: '/internal/admin/get-all-rm',
    ASSIGNRM: '/internal/admin/organizations/:id/rm-mapping',
    GETORGANIZATIONUSERS:
      '/internal/admin/organization/users?organization_id={{organizationId}}&page={{pageNo}}&pageSize={{pageSize}}',
  },
  PARTNER: {
    CREATE: '/internal/admin/partner',
    LIST: '/internal/admin/partner?search={{search}}&partner_type={{partner_type}}&include_inactive={{include_inactive}}&page={{page}}&pageSize={{pageSize}}',
    GET: '/internal/admin/partner/:org_id',
    GET_MEMBERS:
      '/internal/admin/partner/:org_id/members?include_inactive={{include_inactive}}&page={{page}}&pageSize={{pageSize}}',
    ADD_MEMBER: '/internal/admin/partner/:org_id/members',
  },
  LOCATION: {
    LIST: '/internal/locations?loadAll={{loadAll}}&page={{page}}&pageSize={{pageSize}}',
  },
  VENDOR: {
    LIST: '/internal/vendors?active_only={{active_only}}',
    CREATE: '/internal/vendors',
    UPDATE: '/internal/vendors/:id',
  },
  ASSESSMENT_TYPE: {
    LIST: '/internal/assessment-types',
    CREATE: '/internal/assessment-types',
    GET: '/internal/assessment-types/:code',
    UPDATE: '/internal/assessment-types/:code',
    DELETE: '/internal/assessment-types/:code',
  },
  BILLING: {
    LISTPURCHASEPRDERS:
      '/internal/billing/purchase-orders?page={{pageNo}}&pageSize={{pageSize}}&status={{status}}&search={{search}}&sortBy={{sortBy}}&sortOrder={{sortOrder}}',
    BILLINGINFO: '/internal/billing/purchase-orders/:id/details',
    VERIFYPURCHASEORDER: '/internal/billing/purchase-orders/:id/verify',
    LISTMILESTONES: '/internal/billing/milestone-verifications',
    VERIFYMILESTONE: '/internal/billing/milestone-verifications/:id/complete',
    LISTINVOICE:
      '/internal/billing/invoices?invoice_type=INVOICE&page={{pageNo}}&pageSize={{pageSize}}&status={{status}}&search={{search}}&sortBy={{sortBy}}&sortOrder={{sortOrder}}',
    INVOICEINFO: '/internal/billing/invoices/:id',
    VERIFYINVOICE: '/internal/billing/invoices/:id/verify',
    CONFIRMINVOICEPAYMENT: '/internal/billing/invoices/:id/record-payment',
    LISTPROFORMAINVOICE:
      '/internal/billing/invoices?invoice_type=PROFORMA&page={{pageNo}}&pageSize={{pageSize}}&status={{status}}&search={{search}}&sortBy={{sortBy}}&sortOrder={{sortOrder}}',
    PROFORMAINVOICEINFO: '/internal/billing/invoices/:id',
    VERIFYPROFORMAINVOICE: '/internal/billing/invoices/:id/verify',
    CONFIRMPROFORMAINVOICEPAYMENT:
      '/internal/billing/invoices/:id/record-payment',
    CREDITNOTEINFO: '/internal/billing/credit-notes/:id',
    CREDITNOTELIST:
      '/internal/billing/credit-notes?status={{status}}&page={{pageNo}}&pageSize={{pageSize}}',
    APPROVECREDITNOTE: '/internal/billing/credit-notes/:id/approve',
    INVOICEDISPUTES: '/internal/billing/invoices/:id/dispute',
    BILLING_SUMMARY: '/internal/billing/summary',
  },
  REFERENCE_DATA: {
    GET: '/internal/reference-data',
  },
  RESUME_TRACKER: {
    DASHBOARD:
      '/crawler/resume-tracker/dashboard?date_from={{date_from}}&date_to={{date_to}}&page={{page}}&pageSize={{pageSize}}',
    JOB_CANDIDATES:
      '/crawler/resume-tracker/jobs/:jobRef/candidates?date_from={{date_from}}&date_to={{date_to}}&status={{status}}&page={{page}}&pageSize={{pageSize}}',
  },
  INTERVIEWER: {
    // status: 'upcoming' | 'verdict_pending' | 'completed' — drives this
    // page's three tabs 1:1. Confirmed live against api-dev: the query key
    // is lowercase `status` despite how it reads in some docs/specs.
    // search/from_datetime/to_datetime are all server-side per the backend's
    // own route signature — search matches requirement name, requirement
    // registration id, or candidate name; from/to_datetime bound
    // interview.start_time (ISO 8601, UTC if no offset given).
    LIST_INTERVIEWS:
      '/internal/candidates-pipeline/interviews?status={{status}}&page={{page}}&pageSize={{pageSize}}&search={{search}}&from_datetime={{fromDatetime}}&to_datetime={{toDatetime}}',
    // Requirement + candidate + match + interview logistics + timeline +
    // scorecards + sibling-interview list, all in one call, keyed by the
    // same interview_round_detail_id used across this feature.
    GET_BRIEF: '/internal/interviews/:interviewRoundDetailId/brief',
    // The generated question bank (verify/clarify) for that same interview.
    GET_QUESTIONNAIRE:
      '/internal/interviews/:interviewRoundDetailId/questionnaire',
    // Saves one question's rating + comment. `questionnaireId` is that
    // question's own numeric `id` from the GET_QUESTIONNAIRE response (not
    // the "q1"-style display code).
    SUBMIT_QUESTION_RESPONSE:
      '/internal/interviews/:interviewRoundDetailId/questionnaire/:questionnaireId/response',
    // Overall scorecard: recommendation + summary comment.
    SUBMIT_VERDICT: '/internal/interviews/:interviewRoundDetailId/verdict',
    // The interviewer's own private scratch notes for this interview —
    // { comments: "markdown..." } on both GET and PUT. Separate from
    // SUBMIT_VERDICT's comments (that's the final-call summary; this is a
    // freeform sticky-note pad the interviewer can edit throughout).
    GET_NOTES: '/internal/interviews/:interviewRoundDetailId/notes',
    PUT_NOTES: '/internal/interviews/:interviewRoundDetailId/notes',
  },
  SKILL_RESOLUTION: {
    GETQUEUE:
      '/internal/admin/skill-resolution/queue?status={{status}}&side={{side}}&min_occurrences={{min_occurrences}}&sort={{sort}}&suggested={{suggested}}&search={{search}}&page={{page}}&pageSize={{pageSize}}',
    GETQUEUEDETAIL: '/internal/admin/skill-resolution/queue/:nameNorm',
    RESOLVENEWSKILL: '/internal/admin/skill-resolution/resolve/new-skill',
    RESOLVEALIAS: '/internal/admin/skill-resolution/resolve/alias',
    RESOLVEMAP: '/internal/admin/skill-resolution/resolve/map',
    RESOLVEBLOCK: '/internal/admin/skill-resolution/resolve/block',
  },

  // Add more endpoints as needed
};
